/**
 * Helpers for the app-wide `forceLoading` spinner that modals raise while a
 * slide transition is in flight.
 *
 * The spinner is raised synchronously and lowered later — usually from a short
 * `setTimeout`, sometimes from an async callback (FileReader, video metadata).
 * That deferred lower is the *only* thing standing between the user and a
 * spinner that never clears, so any code path that short-circuits it becomes
 * responsible for lowering the spinner itself.
 *
 * The spinner is app-wide, so "lower it unconditionally on unmount" is wrong —
 * an unrelated in-flight request may legitimately own it. Callers therefore
 * track whether *they* raised it and pass that in, rather than inferring
 * ownership from whether a timer happens to be pending: the raise can precede
 * the timer by seconds (a multi-MB image read, video metadata decode), and that
 * whole window would otherwise go unprotected.
 */

/**
 * Releases a modal's claim on the app-wide spinner: cancels any pending
 * lower-timer and, if this modal raised the spinner, lowers it now.
 *
 * @param {number|null|undefined} timer - Pending lower-timer handle, if any.
 * @param {boolean} raised - Whether this modal raised the spinner and has not
 *   yet lowered it.
 * @param {Object|null|undefined} app - The App component owning `forceLoading`.
 */
export const releaseForceLoading = (timer, raised, app) => {
	if (timer !== null && timer !== undefined) {
		clearTimeout(timer);
	}

	if (raised && app) {
		app.setState({
			forceLoading: false,
		});
	}
};
